/**
 * O cartão tokenizado NO NAVEGADOR pelo Stripe.js — a metade que falta para a borda
 * receber um `pm_…` em vez do número do cartão.
 *
 * **Por que o script vem de fora do bundle.** `https://js.stripe.com/v3/` tem de ser
 * carregado do domínio da Stripe, sempre: é o que põe os campos do cartão dentro de um
 * iframe deles, de onde o número não é legível por este código nem por nada que ele
 * carregue. Empacotar o script (ou desenhar campo próprio e mandar o número à borda)
 * jogaria a instalação no escopo PCI mais pesado. Por isso nada aqui lê, guarda ou
 * repassa número de cartão — só recebe de volta um `pm_…`, que não serve para cobrar
 * em outra conta.
 *
 * **A chave publicável vem da BORDA**, não de variável de build: ela é por instalação,
 * e `VITE_*` é a mesma para todo mundo que usa a imagem (ver a rota
 * `autocadastro/preparo-do-cartao` e a migração 0170). Ela é pública por desenho —
 * qualquer visitante a lê no código-fonte da página —, o que não a torna irrelevante:
 * a de teste e a de produção não se misturam, e a borda recusa a do ambiente errado.
 *
 * Contratos do Stripe.js conferidos em 22/09/2026 (docs.stripe.com/js):
 * - `Stripe(pk, { locale })` → a instância. `locale: 'pt-BR'` faz a Stripe devolver a
 *   recusa do cartão já em português; sem isso a mensagem chega em inglês na tela.
 * - `stripe.elements()` → `elements.create('card', …)` → `card.mount(elemento)`.
 * - `card.on('change', …)` dá `{ complete, error }` — é o que habilita o botão.
 * - `stripe.createPaymentMethod({ type: 'card', card, billing_details })` →
 *   `{ paymentMethod: { id: 'pm_…' } }` ou `{ error: { message } }`.
 */

const ENDERECO_DO_SCRIPT = 'https://js.stripe.com/v3/';

/* O mínimo do Stripe.js que usamos, tipado à mão. Preferido a somar `@stripe/stripe-js`
   ao pacote: são quatro chamadas, e o tipo aqui é o contrato que este arquivo depende. */
interface CampoDoCartao {
    mount(onde: HTMLElement): void;
    unmount(): void;
    destroy(): void;
    on(evento: 'change', ouvinte: (e: { complete?: boolean; error?: { message?: string } }) => void): void;
}
interface ElementsDaStripe {
    create(tipo: 'card', opcoes: Record<string, unknown>): CampoDoCartao;
}
interface InstanciaDaStripe {
    elements(): ElementsDaStripe;
    createPaymentMethod(pedido: Record<string, unknown>): Promise<{
        paymentMethod?: { id?: string };
        error?: { message?: string };
    }>;
}
type FabricaDaStripe = (chave: string, opcoes?: Record<string, unknown>) => InstanciaDaStripe;

declare global {
    interface Window {
        Stripe?: FabricaDaStripe;
    }
}

const FALHA_AO_CARREGAR = 'Não conseguimos carregar o formulário de cartão. Verifique a conexão e recarregue a página.';

let carregando: Promise<FabricaDaStripe> | null = null;

/**
 * Carrega o Stripe.js uma vez por página.
 *
 * Memorizado na promessa, não no resultado: duas telas pedindo ao mesmo tempo (ou um
 * React em modo estrito, que monta duas vezes) não podem virar duas tags de script.
 */
export function carregarStripeJs(): Promise<FabricaDaStripe> {
    if (window.Stripe) return Promise.resolve(window.Stripe);
    if (carregando) return carregando;

    carregando = new Promise<FabricaDaStripe>((resolver, recusar) => {
        const pronto = () => {
            if (window.Stripe) resolver(window.Stripe);
            else recusar(new Error(FALHA_AO_CARREGAR));
        };
        /* A tag pode já estar no documento — de outra visita a esta tela, ou do
           `index.html` de uma instalação que a pré-carregue. */
        const existente = document.querySelector<HTMLScriptElement>(`script[src="${ENDERECO_DO_SCRIPT}"]`);
        if (existente) {
            existente.addEventListener('load', pronto);
            existente.addEventListener('error', () => recusar(new Error(FALHA_AO_CARREGAR)));
            if (window.Stripe) pronto();
            return;
        }
        const script = document.createElement('script');
        script.src = ENDERECO_DO_SCRIPT;
        script.async = true;
        script.addEventListener('load', pronto);
        script.addEventListener('error', () => {
            /* Falhou: a próxima tentativa tem de poder recomeçar do zero. */
            carregando = null;
            script.remove();
            recusar(new Error(FALHA_AO_CARREGAR));
        });
        document.head.appendChild(script);
    });
    return carregando;
}

export interface DadosDoTitular {
    nome: string;
    email?: string;
    /** CEP do titular, como a tela coleta. Vai como `address.postal_code`. */
    cep?: string;
}

export type Tokenizacao = { ok: true; meioDePagamento: string } | { ok: false; erro: string };

export interface CampoDeCartao {
    /** Põe o campo da Stripe dentro deste elemento. */
    montar(onde: HTMLElement): void;
    /** Avisa quando dá para enviar, e a recusa que a Stripe já escreve para quem digita. */
    aoMudar(ouvinte: (estado: { completo: boolean; erro: string | null }) => void): void;
    /** Troca o cartão digitado por um `pm_…`. Nada de número de cartão volta daqui. */
    tokenizar(titular: DadosDoTitular): Promise<Tokenizacao>;
    /** Solta o iframe da Stripe. Sem isto ele fica no DOM depois de a tela sair. */
    desmontar(): void;
}

/**
 * A aparência do campo tem de ser passada em valor concreto: o campo vive num iframe da
 * Stripe, onde as `var(--token)` desta página não existem. Ler o token computado mantém
 * `styles.css` como fonte única — em vez de repetir a cor aqui e deixá-la envelhecer.
 */
function tinta(nome: string, reserva: string): string {
    if (typeof window === 'undefined') return reserva;
    const lido = getComputedStyle(document.documentElement).getPropertyValue(nome).trim();
    return lido || reserva;
}

function aparencia(): Record<string, unknown> {
    return {
        base: {
            color: tinta('--text-primary', '#0A2A25'),
            fontFamily: tinta('--font-sans', 'Instrument Sans, system-ui, sans-serif'),
            fontSize: '14px',
            '::placeholder': { color: tinta('--text-secondary', '#97AAA3') },
        },
        invalid: { color: tinta('--danger', '#B42318'), iconColor: tinta('--danger', '#B42318') },
    };
}

/**
 * Prepara o campo do cartão com a chave publicável que a borda deu.
 *
 * Erra cedo e alto quando a chave não é publicável: uma `sk_` aqui iria ao navegador de
 * todo mundo. O banco (0170) e a borda já recusam gravar e servir uma, e esta é a
 * terceira porta — a última antes de o valor virar página.
 */
export async function prepararCampoDeCartao(chavePublicavel: string): Promise<CampoDeCartao> {
    if (!chavePublicavel.startsWith('pk_')) {
        throw new Error('A chave do formulário de cartão não é uma chave publicável.');
    }
    const Stripe = await carregarStripeJs();
    const stripe = Stripe(chavePublicavel, { locale: 'pt-BR' });
    /* O CEP é coletado pela nossa tela (CEP brasileiro, com o resto do endereço), então
       o campo da Stripe não pede o dele: dois pedidos de CEP na mesma tela confundem. */
    const campo = stripe.elements().create('card', { style: aparencia(), hidePostalCode: true });

    return {
        montar(onde) {
            campo.mount(onde);
        },
        aoMudar(ouvinte) {
            campo.on('change', (e) => ouvinte({ completo: e.complete === true, erro: e.error?.message ?? null }));
        },
        async tokenizar(titular) {
            const r = await stripe.createPaymentMethod({
                type: 'card',
                card: campo,
                billing_details: {
                    name: titular.nome,
                    ...(titular.email ? { email: titular.email } : {}),
                    ...(titular.cep ? { address: { postal_code: titular.cep, country: 'BR' } } : {}),
                },
            });
            /* A mensagem de recusa da Stripe é escrita para quem está pagando e já vem
               em português pelo `locale` — repassar é melhor que traduzir por cima. */
            if (r.error) return { ok: false, erro: r.error.message ?? 'O cartão não foi aceito. Confira os dados.' };
            const id = r.paymentMethod?.id;
            if (!id) return { ok: false, erro: 'O cartão não foi preparado. Tente de novo.' };
            return { ok: true, meioDePagamento: id };
        },
        desmontar() {
            try {
                campo.unmount();
                campo.destroy();
            } catch {
                /* Já desmontado: nada a fazer. */
            }
        },
    };
}

/**
 * A fábrica do campo, num lugar só — no molde das portas do projeto.
 *
 * O Stripe.js não carrega no jsdom (e não deveria: baixaria script de terceiro num
 * teste), então quem testa a tela do pagamento troca isto por um campo falso, como já
 * faz com `definirAutocadastro`.
 */
let fabricaDoCampo: (chavePublicavel: string) => Promise<CampoDeCartao> = prepararCampoDeCartao;

export function definirCampoDeCartao(nova: (chavePublicavel: string) => Promise<CampoDeCartao>): void {
    fabricaDoCampo = nova;
}

export function campoDeCartao(chavePublicavel: string): Promise<CampoDeCartao> {
    return fabricaDoCampo(chavePublicavel);
}
